import { Component, ErrorInfo, ReactNode } from 'react';
import { GlobalErrorHandler } from '@/shared/global-error-handler';
import { SecureLogger } from '@/shared/secure-logger';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  showDetails: boolean;
}

/**
 * Error Boundary Component
 * Catches errors in child components and displays fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showDetails: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    SecureLogger.error('Uncaught error:', error, errorInfo);
    GlobalErrorHandler.handleError(error, {
      componentStack: errorInfo.componentStack || '',
    });
  }

  private handleRefresh = (): void => {
    window.location.reload();
  };

  private toggleDetails = (): void => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  public render(): ReactNode {
    const { hasError, error, showDetails } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return children;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#f5f5f5] p-5">
        <h1 className="mb-3 text-2xl font-semibold text-[#2d2d2d]">
          Oops! Something went wrong.
        </h1>
        <p className="mb-6 text-base text-[#666]">
          We&apos;re sorry for the inconvenience. Please try refreshing the page.
        </p>

        <div className="flex gap-3">
          <Button type="button" onClick={this.handleRefresh}>
            Refresh Page
          </Button>
          {error && (
            <Button
              type="button"
              variant="secondary"
              onClick={this.toggleDetails}>
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>
          )}
        </div>

        {showDetails && error && (
          <div className="mt-6 w-full max-w-[800px] rounded border border-[#ddd] bg-white p-4 shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
            <div className="mb-2 font-mono text-sm font-bold text-[var(--inv-accent)]">
              {error.name}: {error.message}
            </div>
            {error.stack && (
              <pre className="m-0 overflow-auto break-words font-mono text-xs whitespace-pre-wrap text-[#2d2d2d]">
                {error.stack}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
